import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit2,
  Lock,
  GripVertical,
  StickyNote as StickyNoteIcon
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * StickyNote component for Timeline 2.0
 *
 * Displays a compact sticky note that can be expanded/collapsed, edited, deleted,
 * and repositioned via drag-and-drop. Width is based on content, not full-width.
 *
 * When isFloating=true, uses smooth mouse-based dragging for free positioning.
 * When isFloating=false, uses dnd-kit sortable for timeline position changes.
 */
const StickyNote = ({
  note,
  onToggleExpand,
  onEdit,
  onDelete,
  isDraggable = true,
  currentUserId,
  isFloating = false,
  onPositionChange,  // Callback for floating notes within same component: (noteId, offsetX, offsetY) => void
  onMoveToComponent, // Callback to move note to a different component: (noteId, componentId, offsetX, offsetY) => void
  containerRef,      // Ref to the container element for calculating bounds
  currentComponentId // ID of the component this note is currently inside
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  const noteRef = useRef(null);

  const isCreator = note.created_by_id === currentUserId;
  const canEdit = !note.is_confidential || isCreator;
  const canDelete = isCreator;

  // Show content when hovered OR when explicitly expanded
  const showContent = isHovered || note.is_expanded;

  // Set up sortable for non-floating notes (timeline positioning)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: `sticky-note-${note.id}`,
    disabled: !isDraggable || isFloating, // Disable sortable for floating notes
  });

  // Handle mouse down for floating notes - start custom drag
  const handleMouseDown = useCallback((e) => {
    if (!isFloating || !isDraggable) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = noteRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: rect.left,
      startY: rect.top
    };

    setIsDragging(true);
    setDragPosition({ x: 0, y: 0 });
  }, [isFloating, isDraggable]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    setDragPosition({ x: deltaX, y: deltaY });
  }, [isDragging]);

  // Handle mouse up - end drag and save position
  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;

    setIsDragging(false);

    // Check if we're over a different component card
    const elementsAtPoint = document.elementsFromPoint(e.clientX, e.clientY);
    let targetComponentId = null;
    let targetComponentRect = null;

    for (const el of elementsAtPoint) {
      const componentCard = el.closest('[data-component-id]');
      if (componentCard) {
        targetComponentId = componentCard.getAttribute('data-component-id');
        targetComponentRect = componentCard.getBoundingClientRect();
        break;
      }
    }

    // Calculate offset position
    if (targetComponentId && targetComponentRect) {
      const relativeX = e.clientX - targetComponentRect.left;
      const relativeY = e.clientY - targetComponentRect.top;
      const offsetX = Math.max(0, Math.min(90, (relativeX / targetComponentRect.width) * 100));
      const offsetY = Math.max(0, Math.min(90, (relativeY / targetComponentRect.height) * 100));

      // Check if dropped on a different component
      if (targetComponentId !== currentComponentId && onMoveToComponent) {
        // Move to different component
        onMoveToComponent(note.id, targetComponentId, Math.round(offsetX), Math.round(offsetY));
      } else if (onPositionChange) {
        // Same component, just update offset
        onPositionChange(note.id, Math.round(offsetX), Math.round(offsetY));
      }
    } else if (containerRef?.current && noteRef.current && onPositionChange) {
      // Fallback: dropped outside any component, update within current container
      const containerRect = containerRef.current.getBoundingClientRect();
      const noteRect = noteRef.current.getBoundingClientRect();

      const finalLeft = noteRect.left + dragPosition.x - containerRect.left;
      const finalTop = noteRect.top + dragPosition.y - containerRect.top;

      const offsetX = Math.max(0, Math.min(90, (finalLeft / containerRect.width) * 100));
      const offsetY = Math.max(0, Math.min(90, (finalTop / containerRect.height) * 100));

      onPositionChange(note.id, Math.round(offsetX), Math.round(offsetY));
    }

    setDragPosition({ x: 0, y: 0 });
  }, [isDragging, dragPosition, containerRef, onPositionChange, onMoveToComponent, note.id, currentComponentId]);

  // Attach global mouse listeners during drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleToggleExpand = async (e) => {
    e.stopPropagation();
    if (onToggleExpand) {
      await onToggleExpand(note.id, !note.is_expanded);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (canEdit && onEdit) {
      onEdit(note);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (canDelete && onDelete) {
      onDelete(note.id);
    }
  };

  // Compute style based on drag mode
  const computedStyle = isFloating
    ? {
        transform: isDragging
          ? `translate(${dragPosition.x}px, ${dragPosition.y}px)`
          : undefined,
        opacity: isDragging ? 0.9 : 1,
        cursor: isDragging ? 'grabbing' : undefined,
        zIndex: isDragging ? 100 : 20,
      }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
      };

  // Combine refs for floating notes
  const setRefs = useCallback((node) => {
    noteRef.current = node;
    if (!isFloating) {
      setNodeRef(node);
    }
  }, [isFloating, setNodeRef]);

  const noteContent = (
    <motion.div
      ref={setRefs}
      style={computedStyle}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className={`
        relative inline-flex flex-col
        bg-ui-amber-bg border border-ui-amber-line rounded-lg shadow-sm
        ${isDragging || isSortableDragging ? 'shadow-lg ring-2 ring-ui-amber-solid' : ''}
        ${isHovered ? 'shadow-md z-30 max-w-xs' : 'max-w-[150px]'}
        ${isFloating ? 'shadow-md' : ''}
        transition-all duration-200
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Drag handle */}
      {isDraggable && (
        <div
          {...(isFloating ? {} : { ...attributes, ...listeners })}
          onMouseDown={isFloating ? handleMouseDown : undefined}
          className={`absolute left-0 top-0 bottom-0 w-5 flex items-center justify-center
            ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
            bg-ui-amber-line/50 rounded-l-lg hover:bg-ui-amber-line transition-colors`}
        >
          <GripVertical className="w-3 h-3 text-ui-amber-fg" />
        </div>
      )}

      <div className={`${isDraggable ? 'pl-6' : 'pl-2'} pr-2 py-1.5`}>
        {/* Header row */}
        <div className="flex items-center gap-1.5">
          <StickyNoteIcon className="w-3.5 h-3.5 text-ui-amber-fg flex-shrink-0" />
          <span className={`font-medium text-ui-amber-fg text-xs ${isHovered ? '' : 'truncate'}`}>
            {note.headline}
          </span>
          {note.is_confidential && (
            <Lock className="w-3 h-3 text-ui-amber-fg flex-shrink-0" title="Confidential - only visible to you" />
          )}

          {/* Action buttons - inline with headline */}
          <div className="flex items-center gap-0.5 ml-1 flex-shrink-0">
            {/* Expand/Collapse button - only show if there's content */}
            {note.content && (
              <button
                onClick={handleToggleExpand}
                className="p-0.5 rounded hover:bg-ui-amber-line transition-colors"
                title={note.is_expanded ? 'Collapse' : 'Expand'}
              >
                {note.is_expanded ? (
                  <ChevronUp className="w-3.5 h-3.5 text-ui-amber-fg" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-ui-amber-fg" />
                )}
              </button>
            )}

            {/* Edit button - show on hover */}
            {canEdit && isHovered && (
              <button
                onClick={handleEdit}
                className="p-0.5 rounded hover:bg-ui-amber-line transition-colors"
                title="Edit note"
              >
                <Edit2 className="w-3.5 h-3.5 text-ui-amber-fg" />
              </button>
            )}

            {/* Delete button - show on hover */}
            {canDelete && isHovered && (
              <button
                onClick={handleDelete}
                className="p-0.5 rounded hover:bg-ui-red-line transition-colors"
                title="Delete note"
              >
                <Trash2 className="w-3.5 h-3.5 text-ui-red-fg" />
              </button>
            )}
          </div>
        </div>

        {/* Content - show on hover or when expanded */}
        {showContent && note.content && (
          <div className="mt-1.5 pt-1.5 border-t border-ui-amber-line/50">
            <p className="text-xs text-ui-amber-fg whitespace-pre-wrap">
              {note.content}
            </p>
            {/* Creator info */}
            <div className="mt-1 text-[10px] text-ui-amber-fg">
              by {note.created_by_name}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );

  // Floating notes don't need wrapper margins
  if (isFloating) {
    return noteContent;
  }

  // Non-floating notes get wrapper with margins
  return (
    <div className="flex justify-start my-2 ml-4">
      {noteContent}
    </div>
  );
};

export default StickyNote;
